/**
 * Release flags — gate V3-only features that are not yet ready to ship.
 *
 * Each feature listed here either has no TruSpace V2 (legacy) equivalent in the
 * product navigation, or is not yet polished enough to release/demo. Most flags
 * default to OFF; `support-zendesk` defaults ON (Stage uses Zendesk). Set the
 * matching `VITE_FEATURE_*` variable to "false" to hide a defaulted-on feature.
 *
 * When a feature is finished, delete its entry here and remove the gate at its
 * call sites (sidebar nav item + route). The feature then becomes permanently
 * visible with no flag left behind.
 *
 * This is a deliberately small, build-time/static gate. It is intentionally
 * distinct from the two other flag surfaces in the app, which solve different
 * problems:
 *   - `lib/feature-flags.ts` — string-keyed LaunchDarkly runtime stub.
 *   - `CompanyFeatureFlags` in `stores/auth.store.ts` — per-company tenant toggles.
 *
 * Reads are written as literal `import.meta.env.VITE_*` member access so Vite
 * statically inlines them into the production bundle. Dynamic indexing such as
 * `import.meta.env[key]` is NOT replaced by Vite and would be `undefined` in a
 * production build, so it is avoided here on purpose.
 */

const ENABLED = 'true';

/** The set of features currently gated behind a release flag. */
export type ReleaseFlag =
  | 'opportunities'
  | 'vault-reports'
  | 'analytics-nav'
  | 'messages'
  | 'admin-v3-extras'
  | 'support-zendesk';

/**
 * Human-readable metadata for each release flag. Drives nothing today, but
 * documents the flag set in one place and is ready to power a future dev/admin
 * toggle surface.
 */
export const RELEASE_FLAG_DEFINITIONS: ReadonlyArray<{
  flag: ReleaseFlag;
  label: string;
  envVar: string;
  reason: string;
}> = [
  {
    flag: 'opportunities',
    label: 'Opportunities',
    envVar: 'VITE_FEATURE_OPPORTUNITIES',
    reason: 'V3-only concept; not ready to release or demo. No legacy V2 nav equivalent.',
  },
  {
    flag: 'vault-reports',
    label: 'Vault Reports',
    envVar: 'VITE_FEATURE_VAULT_REPORTS',
    reason: 'New in V3; legacy vault reports lived in the separate DrawingVault app, never embedded.',
  },
  {
    flag: 'analytics-nav',
    label: 'Analytics (sidebar shortcut)',
    envVar: 'VITE_FEATURE_ANALYTICS_NAV',
    reason:
      'No legacy "Analytics" nav. The entry is only a deep-link alias to Reports → Value Opportunity; the underlying Reports tab stays visible.',
  },
  {
    flag: 'messages',
    label: 'Messages',
    envVar: 'VITE_FEATURE_MESSAGES',
    reason: 'No legacy inbox UI (only alerts + a contact form). Not ready to release.',
  },
  {
    flag: 'admin-v3-extras',
    label: 'Admin V3 extras',
    envVar: 'VITE_FEATURE_ADMIN_V3_EXTRAS',
    reason:
      'Admin submenu items with no legacy V2 nav equivalent (Property Imports, Subscription Limits, Widget Permissions, Report Reviews, Pricing, Domains, Subscriptions, Email Templates). Parked for post-V3.0; pages and APIs stay in the codebase. Drawing Vault Admin is NOT gated here — SSI-only via permissions.',
  },
  {
    flag: 'support-zendesk',
    label: 'Support (Zendesk)',
    envVar: 'VITE_FEATURE_SUPPORT_ZENDESK',
    reason:
      'When ON (default), Support uses Zendesk Help Center SSO + Messenger (legacy parity) and the native SupportPage / My Open Support Requests widget stay dormant. Set VITE_FEATURE_SUPPORT_ZENDESK=false to use native V3 support.',
  },
];

/**
 * Whether a release-gated feature is enabled in the current build.
 * Most flags default to `false` and flip on only when the matching
 * `VITE_FEATURE_*` variable is exactly the string "true".
 * `support-zendesk` defaults ON unless explicitly set to "false".
 */
export function isReleaseFeatureEnabled(flag: ReleaseFlag): boolean {
  switch (flag) {
    case 'opportunities':
      return import.meta.env.VITE_FEATURE_OPPORTUNITIES === ENABLED;
    case 'vault-reports':
      return import.meta.env.VITE_FEATURE_VAULT_REPORTS === ENABLED;
    case 'analytics-nav':
      return import.meta.env.VITE_FEATURE_ANALYTICS_NAV === ENABLED;
    case 'messages':
      return import.meta.env.VITE_FEATURE_MESSAGES === ENABLED;
    case 'admin-v3-extras':
      return import.meta.env.VITE_FEATURE_ADMIN_V3_EXTRAS === ENABLED;
    case 'support-zendesk':
      return import.meta.env.VITE_FEATURE_SUPPORT_ZENDESK !== 'false';
  }
}
